/**
 * Mini 趋势图组件 - 用于 MetricCard 展示时间序列趋势
 * 文档: src/pages/opinion/specs/003-ads-overview/components/MetricCard/MetricTrendChart.md
 */
import React, { useMemo } from 'react';
import EChartCommon from '@op/components/EChartCommon';
import { getChartsGradientColor } from '@op/utils/chart';
import { DBO_EC, DBO_V } from '@op/utils/format';
import { formatChartParamsDate } from '@op/components/ChartCommon/formatter';
import useLanguage from '@op/hooks/useLanguage';
import './index.scss';

interface ITimelineItem {
  time: string;
  value: number;
  oldValue?: number;
}

interface ISeriesConfig {
  name: string;
  color: string;
  lineType?: 'solid' | 'dashed';
}

interface IMetricTrendChartProps {
  data?: ITimelineItem[];
  seriesConfig?: ISeriesConfig[];
  height?: number;
  hasArea?: boolean;
  loading?: boolean;
  smooth?: boolean;
  showTooltip?: boolean;
}

// 默认系列配置
const DEFAULT_SERIES_CONFIG: ISeriesConfig[] = [
  { name: 'This time period', color: '#5086f3', lineType: 'solid' },
  {
    name: 'The previous corresponding period',
    color: '#69d19c',
    lineType: 'dashed',
  },
];

const MetricTrendChart: React.FC<IMetricTrendChartProps> = ({
  data,
  seriesConfig = DEFAULT_SERIES_CONFIG,
  height = 60,
  hasArea = true,
  loading,
  smooth = true,
  showTooltip = true,
}) => {
  const { formatMessage } = useLanguage();
  // ==================== 图表配置 ====================
  const chartOption = useMemo(() => {
    if (!data?.length) return null;

    const xAxisData = data.map((t) => t.time);
    const currentData = data.map((t) => t.value);
    const hasOldData = data.some((t) => t.oldValue !== undefined);
    const oldData = hasOldData ? data.map((t) => t.oldValue ?? null) : [];

    // 计算最大值用于 Y 轴
    const allValues = [...currentData, ...oldData.filter((v) => v !== null)];
    const maxValue = Math.max(
      ...allValues.filter((v) => v !== null && v !== undefined),
    );

    // 构建系列数据
    const series: any[] = [];

    // 当前周期系列 - 只在最后一个点显示 symbol
    const currentSeriesData = currentData.map((value, index) => ({
      value,
      symbol: index === currentData.length - 1 ? 'circle' : 'none',
      symbolSize: index === currentData.length - 1 ? 9 : 0,
      itemStyle: {
        opacity: index === currentData.length - 1 ? 1 : 0,
      },
    }));

    const currentSeries: any = {
      name: seriesConfig[0]?.name || 'This time period',
      type: 'line',
      data: currentSeriesData,
      showSymbol: true,
      lineStyle: {
        color: seriesConfig[0]?.color || '#5086f3',
        type: seriesConfig[0]?.lineType || 'solid',
      },
    };

    // 添加填充区域
    if (hasArea) {
      currentSeries.areaStyle = getChartsGradientColor(
        seriesConfig[0]?.color || '#5086f3',
        0,
        0.3,
      );
    }

    series.push(currentSeries);

    // 对比周期系列（如果有）
    if (hasOldData && seriesConfig[1]) {
      const oldSeries: any = {
        name: seriesConfig[1].name,
        type: 'line',
        data: oldData,
        smooth,
        symbol: 'none',
        lineStyle: {
          color: seriesConfig[1].color,
          type: seriesConfig[1].lineType || 'dashed',
        },
      };

      if (hasArea) {
        oldSeries.areaStyle = getChartsGradientColor(
          seriesConfig[1].color,
          0,
          0.2,
        );
      }

      series.push(oldSeries);
    }

    return {
      tooltip: {
        trigger: 'axis',
        confine: false,
        position: (
          point: number[],
          _params: any,
          _dom: HTMLElement,
          rect: any,
          size: { contentSize: number[]; viewSize: number[] },
        ) => {
          // place tooltip above the chart, horizontally centered on cursor
          const tooltipH = size.contentSize[1];
          const tooltipW = size.contentSize[0];
          const x = Math.max(0, Math.min(point[0] - tooltipW / 2, size.viewSize[0] - tooltipW));
          const y = -tooltipH;
          return [x, y];
        },
        formatter: (params: any) =>
          formatChartParamsDate({
            params,
            title: params[0]?.axisValue,
            valueFormat: (v: number) => DBO_V.reviews(v),
            listTitleFunc: (item) => formatMessage({ id: item.seriesName }),
          }),
      },
      grid: { top: 20, bottom: 0, left: 20, right: 5 },
      xAxis: {
        type: 'category',
        show: true,
        data: xAxisData,
        boundaryGap: false,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          show: true,
          fontSize: 10,
          color: '#8b94a8',
          interval: 'auto' as const,
          rotate: 0,
          formatter: DBO_EC.timeX,
        },
      },
      legend: { show: false },
      yAxis: {
        type: 'value',
        show: true,
        position: 'right',
        max: maxValue > 0 ? maxValue : undefined,
        splitNumber: 3,
        splitLine: {
          show: false,
        },
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          show: true,
          fontSize: 10,
          color: '#8b94a8',
          formatter: (value) => DBO_EC.reviews(value),
        },
      },
      series,
    };
  }, [data, seriesConfig, hasArea, smooth, showTooltip, formatMessage]);

  // ==================== 渲染逻辑 ====================
  return (
    <div className="dbo-metric-trend-chart">
      <EChartCommon
        options={chartOption}
        hasEmptyImg={false}
        optionsProps={{ type: 'line' }}
        height={height}
        empty={!data?.length || !data.some((t) => t.value > 0)}
        loading={loading}
      />
    </div>
  );
};

export default React.memo(MetricTrendChart);
